/**
 * safeStorage — Web Storage that can never throw.
 *
 * `window.sessionStorage` / `window.localStorage` are not always there for the
 * taking. Reading the *property itself* throws a SecurityError when the document
 * can't have storage: a sandboxed iframe without `allow-same-origin`, a browser
 * set to block all site data, some in-app webviews, and third-party contexts
 * under Chrome's storage partitioning. Android in-app browsers hit this in the
 * wild, which is how a blog post ended up throwing
 * "Failed to read the 'sessionStorage' property from 'Window'" at real visitors.
 *
 * Writes have their own failure mode — Safari private mode and a full quota both
 * throw QuotaExceededError on `setItem`.
 *
 * So every access goes through here: the property lookup and the call are both
 * inside the try, and a failure degrades to "no storage" rather than an error
 * screen. Storage is a convenience on this site (a dismissed popup, a theme, a
 * reload guard) — never something worth breaking a page over.
 */

type StorageKind = "localStorage" | "sessionStorage";

/** Both the property access and the call must be guarded — either can throw. */
function withStorage<T>(kind: StorageKind, fn: (s: Storage) => T, fallback: T): T {
  try {
    const store = window[kind];
    // Blocked storage sometimes surfaces as null rather than a throw.
    if (!store) return fallback;
    return fn(store);
  } catch {
    return fallback;
  }
}

export interface SafeStorage {
  /** The stored string, or null if absent *or* storage is unavailable. */
  getItem(key: string): string | null;
  /** True if the value was actually persisted. */
  setItem(key: string, value: string): boolean;
  removeItem(key: string): void;
  /** Whether this storage can be read and written at all. */
  isAvailable(): boolean;
}

function makeSafeStorage(kind: StorageKind): SafeStorage {
  return {
    getItem: (key) => withStorage(kind, (s) => s.getItem(key), null),
    setItem: (key, value) =>
      withStorage(
        kind,
        (s) => {
          s.setItem(key, value);
          return true;
        },
        false,
      ),
    removeItem: (key) => {
      withStorage(kind, (s) => s.removeItem(key), undefined);
    },
    isAvailable: () => withStorage(kind, (s) => typeof s.getItem === "function", false),
  };
}

export const safeLocalStorage = makeSafeStorage("localStorage");
export const safeSessionStorage = makeSafeStorage("sessionStorage");
